import { RowData } from '@flatfile/turntable'
import { BasicRecord } from '../api/records/types'
import { RowChangeDetail } from './interface'

/**
 * Converts a row change to record data format needed for API
 * @param change Row change detail
 * @param columnConfig Column configuration
 * @returns Record data in API format
 */
export function recordData(change: RowChangeDetail, columnConfig: any[]) {
  const data: { [key: string]: { value: any } } = {}

  Object.keys(change).forEach((key) => {
    if (key !== 'id' && key !== 'metadata' && key !== 'config') {
      data[key] = { value: change[key] }
    }
  })

  return data
}

/**
 * Formats concise transformed records to Turntable row format
 * @param transformedRecords Array of concise transformed records
 * @param skip Number of records to skip
 * @param columnConfig Column configuration
 * @returns Formatted rows for Turntable
 */
export function formatTurntableDiffRowsFromConcise(
  records: BasicRecord[],
  transformedRecords: BasicRecord[],
  skip: number,
  columnConfig: any[]
): RowData[] {
  return transformedRecords.map((transformedRecord, index) => {
    const { __k, __e, __n, __s, _source, _deleted, ...transformedValues } =
      transformedRecord
    const values = _source || {}

    // Format cells according to column config
    const cells = columnConfig.map((column, colIndex) => {
      const value = values[column.value]

      const transformedValue = transformedValues[column.value]
      return {
        value: value,
        diffValue: transformedValue,
        dataColumnIndex: colIndex,
        disabled: false, // Could be determined by __e or other metadata
        mutated: false, // Could be determined by comparing with original values
        status: {
          error: _deleted
            ? [{ message: 'This record will be deleted' }]
            : __e
              ? [{ message: String(__e) }]
              : undefined,
          info: undefined,
          warning: undefined,
          diff: undefined,
        },
        metadata: {},
        config: {},
      }
    })

    return {
      id: __k,
      cells,
      position: index + skip,
      rowIndex: index + skip + 1,
    }
  })
}

/**
 * Formats concise transformed records to Turntable row format
 * @param transformedRecords Array of concise transformed records
 * @param skip Number of records to skip
 * @param columnConfig Column configuration
 * @returns Formatted rows for Turntable
 */
export function formatTurntableRowsFromConcise(
  records: BasicRecord[],
  skip: number,
  columnConfig: any[]
): RowData[] {
  return records.map((record, index) => {
    const { __k, __e, __n, __s, _deleted, ...values } = record

    // Format cells according to column config
    const cells = columnConfig.map((column, colIndex) => {
      const value = values[column.value]
      return {
        value: value,
        dataColumnIndex: colIndex,
        disabled: false, // Could be determined by __e or other metadata
        mutated: false, // Could be determined by comparing with original values
        status: {
          error: _deleted
            ? [{ message: 'This record will be deleted' }]
            : __e
              ? [{ message: String(__e) }]
              : undefined,
          info: undefined,
          warning: undefined,
          diff: undefined,
        },
        metadata: {},
        config: {},
      }
    })

    return {
      id: __k,
      cells,
      position: index + skip,
      rowIndex: index + skip + 1,
    }
  })
}
